import { create } from 'zustand';

import type { PDFDocument } from '@recto/pdf-engine';
import type { ProviderFileRef } from '@recto/provider';

// State shape kept intentionally narrow for M1. Expands with editing
// stories: undo history (S-011+), annotation state (S-014+), font
// substitution counter (S-012), etc.

interface DocumentState {
  readonly pdf: PDFDocument | null;
  readonly sourceRef: ProviderFileRef | null;
  readonly originalName: string | null;
  readonly isDirty: boolean;
}

interface DocumentActions {
  readonly setOpenedDoc: (input: {
    readonly pdf: PDFDocument;
    readonly sourceRef: ProviderFileRef;
    readonly originalName: string;
  }) => void;
  readonly markDirty: () => void;
  readonly clearDirty: () => void;
  readonly reset: () => void;
}

const initialState: DocumentState = {
  pdf: null,
  sourceRef: null,
  originalName: null,
  isDirty: false,
};

/**
 * Zustand slice for the currently-open PDF. One-doc-at-a-time in M1
 * — multi-doc merge (S-020) will move this to a `Map<id, DocumentSlot>`.
 *
 * `sourceRef` is opaque per `IStorageProvider` contract; consumers
 * pass it back to the provider unmodified for `upload` / `download` /
 * `getFileMeta`.
 */
export const useDocumentStore = create<DocumentState & DocumentActions>((set) => ({
  ...initialState,

  setOpenedDoc: (input): void => {
    set({
      pdf: input.pdf,
      sourceRef: input.sourceRef,
      originalName: input.originalName,
      isDirty: false,
    });
  },

  markDirty: (): void => {
    set({ isDirty: true });
  },

  clearDirty: (): void => {
    set({ isDirty: false });
  },

  reset: (): void => {
    set(initialState);
  },
}));
